import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Share2 } from "lucide-react";
import { toast } from "../ui/sonner";
import { SignatureForm } from "./SignatureForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHARE_TEXT = "Lettre à celles et ceux qui veulent présider la France — « Je ne vous demande pas un discours. Je vous demande un plan. »";

const share = async () => {
  const url = window.location.origin;
  if (navigator.share) {
    try { await navigator.share({ title: "MM4S — La Grande Question", text: SHARE_TEXT, url }); } catch { /* annulé */ }
    return;
  }
  await navigator.clipboard.writeText(`${SHARE_TEXT} ${url}`);
  toast.success("Lien copié. Vous pouvez le coller où vous voulez.");
};

const shareLinks = (url) => [
  { id: "whatsapp", label: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent(`${SHARE_TEXT} ${url}`)}` },
  { id: "facebook", label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
  { id: "x", label: "X", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TEXT)}&url=${encodeURIComponent(url)}` },
  { id: "linkedin", label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}` },
];

const SignatureList = ({ items }) => (
  <ul className="mt-8 space-y-4 max-h-[420px] overflow-y-auto pr-2" data-testid="signature-list">
    {items.map((s) => (
      <li key={s.id} className="border-l-4 border-mm-red pl-4" data-testid="signature-item">
        <p className="font-bold">{s.name} <span className="font-normal opacity-70">· {s.city}</span></p>
        {s.message && <p className="text-sm mt-1 italic">« {s.message} »</p>}
      </li>
    ))}
  </ul>
);

export const Signer = () => {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    const [s, l] = await Promise.all([axios.get(`${API}/signatures/stats`), axios.get(`${API}/signatures?limit=20`)]);
    setStats(s.data); setItems(l.data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);
  const url = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <section id="signer" className="bg-mm-paper text-mm-ink py-24 md:py-32 border-t border-mm-ink/10" data-testid="signer-section">
      <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-[160px_1fr] lg:grid-cols-[160px_1fr_1fr] gap-12">
        <p className="mm-lab text-mm-golddark">Soutien</p>
        <div className="min-w-0">
          <h2 className="disp mm-h2">La Grande Question vous appartient aussi.</h2>
          <p className="mm-body mt-4 max-w-md">Signez la lettre, laissez votre réaction, et faites-la circuler. Candidats, équipes, journalistes, familles, jeunes : chacun peut la poser.</p>
          {stats && stats.count > 0 ? (
            <div className="mt-8 border-t-4 border-mm-ink pt-4">
              <p className="disp mm-big tab text-mm-red" data-testid="signature-count">{stats.count.toLocaleString("fr-FR")}</p>
              <p className="mm-lab">{stats.count > 1 ? "signatures" : "signature"}</p>
              <SignatureList items={items} />
            </div>
          ) : (
            <div className="mt-8 border-2 border-dashed border-mm-ink/40 p-6" data-testid="signature-empty">
              <p className="disp mm-big text-mm-ink/25">0</p>
              <p className="text-sm">Les premières signatures s'afficheront ici.</p>
            </div>
          )}
          <button type="button" onClick={share} data-testid="share-letter-btn" className="mt-8 inline-flex items-center gap-2 border-2 border-mm-ink px-5 py-3 font-bold hover:bg-mm-ink hover:text-mm-paper transition-colors">
            <Share2 className="w-4 h-4" /> Partager la lettre
          </button>
          <div className="mt-4 flex flex-wrap gap-4 text-sm font-semibold">
            {shareLinks(url).map((l) => (
              <a key={l.id} href={l.href} target="_blank" rel="noopener noreferrer" className="ulink" data-testid={`share-${l.id}`}>{l.label}</a>
            ))}
          </div>
        </div>
        <div className="md:col-start-2 lg:col-start-auto"><SignatureForm onSigned={load} /></div>
      </div>
    </section>
  );
};
